import React from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, Button } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useEnv, EnvName } from './EnvContext';

/**
 * Top-bar items hidden 2026-09-18 at Peter's request: the environment switcher
 * and the Analyse nav button. Sign out stays.
 *
 * Flags rather than commented-out markup, because both blocks contain their own
 * JSX comments and a wrapping brace-slash-star would terminate at the first one
 * and break the render. A flag also makes restoring either a single word.
 *
 * Neither is the only way in: /analysis is reachable by URL, and the analysis
 * environment by the `oc_analysis_env` localStorage key, which is what
 * EnvContext.getActiveEnv reads on load.
 */
const SHOW_ENV_SWITCHER = false;
const SHOW_ANALYSIS_NAV = false;

type TopbarProps = {
  menuState: boolean;
  openAction: (open: boolean) => void;
};

const BUILD_VERSION = process.env.REACT_APP_VERSION ?? 'dev';
const BUILD_DATE    = process.env.REACT_APP_BUILD_DATE ?? '';

const Topbar = (props: TopbarProps) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, signOut } = useAuth();
  const { envName, setEnv } = useEnv();
  const isAnalysis = location.pathname.startsWith('/analysis');

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const toggleEnv = () => {
    setEnv(envName === 'staging' ? 'prod' : 'staging');
  };

  const envIsProd    = envName === 'prod';
  const envLabel     = envIsProd ? 'Prod' : 'Staging';
  const envColour    = envIsProd ? '#4ade80' : '#f0a500';
  const envBg        = envIsProd ? 'rgba(74,222,128,0.08)' : 'rgba(240,165,0,0.08)';
  const envBorder    = envIsProd ? 'rgba(74,222,128,0.4)' : 'rgba(240,165,0,0.4)';
  const envHoverBg   = envIsProd ? 'rgba(74,222,128,0.15)' : 'rgba(240,165,0,0.15)';

  return (
    <AppBar position="fixed" sx={{ backgroundColor: '#192338' }}>
      <IconButton
        size="large"
        edge="end"
        color="inherit"
        aria-label="menu"
        sx={{ position: 'absolute', left: 0, top: '7px', zIndex: 1, display: { lg: 'none' } }}
        onClick={() => props.openAction(!props.menuState)}
      >
        <MenuIcon />
      </IconButton>

      <Toolbar sx={{ justifyContent: 'center' }}>
        <Box
          component="img"
          sx={{ height: 40, marginRight: 2, cursor: 'pointer' }}
          alt="Bahn Stuttgart Logo"
          src="/logo.png"
          onClick={() => navigate('/home')}
        />
      </Toolbar>

      {/* Right-side controls */}
      <Box sx={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}>
        {/* Env switcher — see SHOW_ENV_SWITCHER above */}
        {SHOW_ENV_SWITCHER && (
        <Button
          size="small"
          onClick={toggleEnv}
          title={`Switch to ${envName === 'staging' ? 'prod' : 'staging'}`}
          sx={{
            color: envColour,
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'none',
            letterSpacing: '0.06em',
            border: `1px solid ${envBorder}`,
            borderRadius: '4px',
            padding: '2px 8px',
            backgroundColor: envBg,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            '&:hover': {
              backgroundColor: envHoverBg,
              borderColor: envColour,
            },
          }}
        >
          {/* Pulsing dot */}
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: envColour,
              flexShrink: 0,
              animation: envIsProd ? 'none' : 'ocPulse 2s ease-in-out infinite',
              '@keyframes ocPulse': {
                '0%, 100%': { opacity: 1 },
                '50%':      { opacity: 0.3 },
              },
            }}
          />
          {envLabel}
        </Button>
        )}

        {/* Analyse nav — see SHOW_ANALYSIS_NAV above */}
        {SHOW_ANALYSIS_NAV && (
        <Button
          size="small"
          onClick={() => navigate(isAnalysis ? '/live' : '/analysis')}
          sx={{
            color: isAnalysis ? '#f0a500' : 'rgba(255,255,255,0.7)',
            fontSize: '12px',
            textTransform: 'none',
            letterSpacing: '0.05em',
            border: isAnalysis ? '1px solid #f0a500' : '1px solid rgba(255,255,255,0.2)',
            borderRadius: '4px',
            padding: '2px 10px',
            '&:hover': {
              color: '#f0a500',
              borderColor: '#f0a500',
              backgroundColor: 'rgba(240,165,0,0.08)',
            },
          }}
        >
          {isAnalysis ? '← Live' : 'Analyse'}
        </Button>
        )}

        {/* Sign out */}
        <Button
          size="small"
          onClick={handleSignOut}
          sx={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: '11px',
            textTransform: 'none',
            letterSpacing: '0.05em',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '4px',
            padding: '2px 8px',
            minWidth: 0,
            '&:hover': {
              color: '#ff6b6b',
              borderColor: '#ff6b6b',
              backgroundColor: 'rgba(255,107,107,0.08)',
            },
          }}
        >
          Sign out
        </Button>
      </Box>

      {/* Version + email */}
      <Box sx={{ position: 'absolute', right: 12, bottom: 4, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {user && (
          <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', display: { xs: 'none', md: 'block' } }}>
            {user.email}
          </Typography>
        )}
        <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.03em' }}>
          {BUILD_VERSION}{BUILD_DATE ? ` · ${BUILD_DATE}` : ''}
        </Typography>
      </Box>
    </AppBar>
  );
};

export default Topbar;
